import { useCallback, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { FormInput, FormLabel, FormRow } from '@ui/components/Form/Form';
import { Input } from '@ui/components/Input';
import { ThemeButton } from '@ui/components/ThemeButton';

import { TournamentEditFormValues } from '@features/tournament/ui/components/TournamentSettings/typings';

import styles from './TournamentEditFormInputs.module.css';

export function RoundDelaysInput({ maxRounds }: { maxRounds: number }) {
  const [newRoundNumber, setNewRoundNumber] = useState('1');
  const [newDelay, setNewDelay] = useState('5');

  const {
    control,
    formState: { disabled },
  } = useFormContext<TournamentEditFormValues>();
  const { fields, append, remove, update } = useFieldArray<
    TournamentEditFormValues,
    'customRoundDelays'
  >({
    control,
    name: 'customRoundDelays',
  });

  const onAdd = useCallback(() => {
    if (!newRoundNumber || !newDelay) return;
    append({ roundNumberAfter: newRoundNumber, delayMinutes: newDelay });
    setNewRoundNumber(String(Math.min(maxRounds, Number(newRoundNumber) + 1)));
  }, [newRoundNumber, newDelay, setNewRoundNumber, append, maxRounds]);

  const onRoundPauseUpdate = useCallback(
    (updated: { roundNumberAfter: string; delayMinutes: string }, i: number) => {
      update(i, updated);
    },
    [update],
  );

  return (
    <FormRow>
      <FormLabel>Паузы между турами</FormLabel>
      <FormInput className={styles.roundPauses}>
        {fields.map((f, i) => {
          return (
            <div key={i} className={styles.roundPause}>
              <Input
                className={styles.numInput}
                invisible
                value={f.roundNumberAfter || ''}
                onChange={(newValue) => {
                  onRoundPauseUpdate(
                    {
                      roundNumberAfter: newValue,
                      delayMinutes: f.delayMinutes,
                    },
                    i,
                  );
                }}
                leftLabel="После"
                rightLabel="тура"
                disabled={disabled}
                min="1"
                max={maxRounds.toString()}
              />

              <Input
                className={styles.numInput}
                invisible
                value={f.delayMinutes || ''}
                onChange={(newValue) => {
                  onRoundPauseUpdate(
                    {
                      roundNumberAfter: f.roundNumberAfter,
                      delayMinutes: newValue,
                    },
                    i,
                  );
                }}
                onEnter={onAdd}
                leftLabel="пауза"
                rightLabel="мин"
                disabled={disabled}
              />

              <ThemeButton className={styles.stretch} onClick={() => remove(i)} disabled={disabled}>
                Удалить
              </ThemeButton>
            </div>
          );
        })}

        <div className={styles.roundPause}>
          <Input
            className={styles.numInput}
            invisible
            type="number"
            value={newRoundNumber}
            onChange={setNewRoundNumber}
            leftLabel="После"
            rightLabel="тура"
            disabled={disabled}
            min="1"
            max={maxRounds.toString()}
          />
          <Input
            className={styles.numInput}
            invisible
            type="number"
            value={newDelay}
            onChange={setNewDelay}
            onEnter={onAdd}
            leftLabel="пауза"
            rightLabel="мин"
            disabled={disabled}
          />

          <ThemeButton
            className={styles.stretch}
            onClick={onAdd}
            disabled={disabled || !Number(newRoundNumber) || !Number(newDelay)}
          >
            Добавить
          </ThemeButton>
        </div>
      </FormInput>
    </FormRow>
  );
}
